import { useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Braces, Download } from 'lucide-react'

import { downloadBlob } from '@/lib/download-blob'
import { formatDateTime } from '@/lib/format-date'
import { Button } from '@/ui/button'
import { DetailHeading } from '@/ui/detail-list'

import { reportPreviewApi } from '../api/report-preview-api'
import { usePreviewReport } from '../api/report-preview-queries'
import { REPORT_RUN_STATE_TITLES, reportErrorMessage, reportRunState } from '../lib/report-errors'
import type {
  ReportConditionValue,
  ReportOperator,
  ReportQueryDefinition,
  ReportSort,
  ReportSourceRef,
} from '../lib/report-query'
import { ReportResults, type ReportResultsProps } from './report-results'

export type EmbeddedReportTableProps = {
  entityId: string
  title: string
  columns: { column: string; label?: string }[]
  defaultSort: ReportSort
  formatCell?: ReportResultsProps['formatCell']
  emptyMessage?: ReportResultsProps['emptyMessage']
} & ({
  source: ReportSourceRef
  filter: { column: string; op: ReportOperator; value?: ReportConditionValue }[]
  sql?: never
} | {
  sql: (sort: ReportSort) => string
  source?: never
  filter?: never
})

const ALIAS = 't'

export function EmbeddedReportTable({ entityId, title, source, columns, filter, sql, defaultSort, formatCell, emptyMessage }: EmbeddedReportTableProps) {
  const navigate = useNavigate()
  const [sort, setSort] = useState(defaultSort)
  const [busy, setBusy] = useState<'export' | 'explore' | null>(null)
  const [actionError, setActionError] = useState<unknown>(null)
  const [announcement, setAnnouncement] = useState('')
  const resultsRef = useRef<HTMLDivElement>(null)

  const definition: ReportQueryDefinition | null = source ? {
    from: { alias: ALIAS, ...source },
    columns: columns.map(({ column }) => ({ source: ALIAS, column })),
    filter: filter && filter.length > 0
      ? { and: filter.map(({ column, op, value }) => ({ left: { source: ALIAS, column }, op, ...(value === undefined ? {} : { right: { value } }) })) }
      : undefined,
    sort: [sort],
  } : null
  const rawQuery = sql?.(sort) ?? null
  const preview = usePreviewReport(entityId, definition, rawQuery, null, true)
  const onDefaultSort = sort.field === defaultSort.field && sort.direction === defaultSort.direction
  const canRetry = preview.isError && (!onDefaultSort || ['timeout', 'cancelled', 'failed'].includes(reportRunState(preview.error)))
  const timestampColumns = new Set(preview.data?.columns.filter((column) => column.type === 'timestamp').map((column) => column.name))
  const labels: Record<string, string> = Object.fromEntries(columns.flatMap(({ column, label }) => (label ? [[column, label]] : [])))

  function run(action: 'export' | 'explore', work: () => Promise<string>) {
    if (busy !== null) return
    setBusy(action)
    setActionError(null)
    setAnnouncement(action === 'export' ? 'Exporting CSV…' : 'Opening Query Explorer…')
    void work()
      .then(setAnnouncement)
      .catch((error: unknown) => {
        setAnnouncement('')
        setActionError(error)
      })
      .finally(() => setBusy(null))
  }

  return (
    <section className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-2">
        <DetailHeading>{title}</DetailHeading>
        <div className="ml-auto flex gap-2">
          <Button type="button" variant="outline" size="sm" disabled={!preview.data} aria-disabled={busy !== null} onClick={() =>
            run('export', async () => {
              const file = await reportPreviewApi.exportPreview(entityId, definition, rawQuery)
              downloadBlob(file.blob, file.filename)
              return 'CSV downloaded.'
            })}>
            <Download data-icon="inline-start" />
            {busy === 'export' ? 'Exporting…' : 'Export CSV'}
          </Button>
          <Button type="button" variant="outline" size="sm" disabled={!preview.data} aria-disabled={busy !== null} onClick={() =>
            run('explore', async () => {
              const query = rawQuery ?? (definition ? await reportPreviewApi.previewSql(entityId, definition) : '')
              await navigate({ to: '/$entityId/reports/query', params: { entityId }, search: { sql: query } })
              return ''
            })}>
            <Braces data-icon="inline-start" />
            {busy === 'explore' ? 'Opening…' : 'Open in Query Explorer'}
          </Button>
        </div>
      </div>
      <p role="status" className="sr-only">{announcement}</p>
      {actionError ? (
        <div role="alert" data-run-state={reportRunState(actionError)} className="text-xs">
          <p className="text-destructive font-medium">{REPORT_RUN_STATE_TITLES[reportRunState(actionError)]}</p>
          <p className="text-muted-foreground mt-1">{reportErrorMessage(actionError)}</p>
        </div>
      ) : null}
      {canRetry ? (
        <Button type="button" variant="outline" size="sm" className="self-start" onClick={() => {
          setAnnouncement('Retrying…')
          resultsRef.current?.focus()
          if (onDefaultSort) void preview.refetch()
          else setSort(defaultSort)
        }}>
          Try again
        </Button>
      ) : null}
      <div ref={resultsRef} tabIndex={-1} aria-busy={preview.isFetching} className="border-border flex max-h-96 min-h-0 flex-col overflow-hidden rounded-md border">
        <ReportResults
          result={preview.data}
          error={preview.error}
          isPending={preview.isPending}
          hasRun
          labels={labels}
          truncatedMessage="Export CSV to get every row."
          emptyMessage={emptyMessage}
          formatCell={(column, value) =>
            formatCell?.(column, value) ?? (typeof value === 'string' && timestampColumns.has(column) && !Number.isNaN(Date.parse(value)) ? formatDateTime(value) : undefined)
          }
          sort={{
            ...sort,
            onSort: (field) => {
              if (preview.isFetching) return
              const direction = sort.field === field && sort.direction === 'asc' ? 'desc' : 'asc'
              setActionError(null)
              setAnnouncement(`Sorting by ${labels[field] ?? field}, ${direction === 'asc' ? 'ascending' : 'descending'}.`)
              setSort({ field, direction })
            },
          }}
        />
      </div>
    </section>
  )
}
